import { ElementRef } from '@angular/core';
import { LanguageServices } from 'src/app/services/LanguageServices';
import { ChamadosService } from '../services/chamados.service';
import { Ticket, TicketAttachment, TicketMessage, TicketStatus } from '../models/chamados.models';
import { authorColor, buildTimeline, eventIcon, TimelineEntry } from '../mesa/timeline';

/**
 * Apresentacao da conversa, compartilhada pelas telas que a exibem.
 *
 * Duas telas mostram a MESMA conversa: a mesa de atendimento e o
 * acompanhamento do solicitante. O que muda entre elas nao e o desenho — e o
 * que cada uma pode fazer com o chamado, e se as notas internas aparecem.
 * Por isso so a apresentacao mora aqui; carregar, filtrar e agir continua em
 * cada tela.
 *
 * NAO leva decorator Angular de proposito: nao tem ciclo de vida, input nem
 * injecao propria. Quem estende passa o `LanguageServices` no super().
 */
export abstract class ConversaBase {

    timeline: TimelineEntry[] = [];
    carregandoConversa = false;

    /**
     * Nota interna e comunicacao ENTRE ATENDENTES. Fica falso por padrao: a
     * tela que quiser mostra-la tem que dizer isso explicitamente, e nao o
     * contrario — o erro caro aqui e vazar nota interna para o cliente.
     */
    protected mostrarInternas = false;

    /**
     * O elemento que ROLA na conversa.
     *
     * Cada tela marca o seu com `#corpoDaConversa` e um `@ViewChild` — a base
     * nao tem como alcanca-lo sozinha, porque nao tem template nem injecao
     * propria.
     */
    protected corpoDaConversa: ElementRef<HTMLElement>;

    /**
     * O servico, para pedir o link assinado do anexo.
     *
     * Opcional para nao obrigar quem so exibe conversa a injeta-lo — sem ele,
     * anexo com chave aparece sem link, que e o comportamento de antes.
     */
    constructor(public ls: LanguageServices, protected chamadosService?: ChamadosService) { }

    /**
     * Abre o anexo.
     *
     * Dois formatos convivem: anexo antigo guarda a URL no `ref` e abre
     * direto; anexo novo guarda a CHAVE, e o link e assinado na hora — dura
     * minutos e so sai para quem pode ver o chamado.
     *
     * A JANELA E ABERTA ANTES da consulta, de proposito: `window.open` depois
     * de um `await` e bloqueado como pop-up pelo navegador, porque perdeu o
     * vinculo com o clique. Abre-se em branco e troca-se o endereco quando o
     * link chega.
     */
    abrirAnexo(anexo: TicketAttachment, ticketId: string): void {

        const direto = this.linkDoAnexo(anexo);

        if (direto != null) {
            window.open(direto, '_blank', 'noopener');
            return;
        }

        const ref = (anexo?.ref ?? '').trim();

        if (ref.length === 0 || this.chamadosService == null || ticketId == null) {
            return;
        }

        const janela = window.open('', '_blank');

        this.chamadosService.urlDoAnexo(ticketId, ref).subscribe({
            next: url => {

                if (url == null || url.trim().length === 0) {
                    janela?.close();
                    return;
                }

                if (janela != null) {
                    janela.location.href = url;
                } else {
                    // Pop-up bloqueado mesmo assim: melhor navegar na propria
                    // aba do que nao abrir nada.
                    window.location.href = url;
                }
            },
            error: () => janela?.close(),
        });
    }

    protected montarTimeline(ticket: Ticket, mensagens: TicketMessage[]): void {
        this.timeline = buildTimeline(ticket, mensagens, this.mostrarInternas);
    }

    /**
     * Desce para a ultima mensagem.
     *
     * Conversa se le de baixo para cima: abrir no topo obriga a rolar ate o
     * fim toda vez, e o que interessa e sempre o que acabou de chegar.
     *
     * `forcar` diz de onde veio a chamada. Ao ABRIR a conversa, sempre desce.
     * Num refresh automatico, so desce se a pessoa ja estava no fim — puxar a
     * tela de quem esta lendo o historico e pior do que deixar a mensagem
     * nova esperando.
     *
     * O `setTimeout` espera o Angular pintar a timeline nova: sem ele o
     * `scrollHeight` ainda e o da lista anterior, e a rolagem para no meio.
     */
    protected rolarParaOFim(forcar: boolean = false): void {

        if (!forcar && !this.pertoDoFim()) {
            return;
        }

        setTimeout(() => {

            const elemento = this.corpoDaConversa?.nativeElement;

            if (elemento == null) {
                return;
            }

            elemento.scrollTop = elemento.scrollHeight;
        });
    }

    /** Ja estava no fim? Uma mensagem de folga conta como "no fim". */
    private pertoDoFim(): boolean {

        const elemento = this.corpoDaConversa?.nativeElement;

        if (elemento == null) {
            return true;
        }

        const distancia = elemento.scrollHeight - elemento.scrollTop - elemento.clientHeight;

        return distancia < 120;
    }

    /** Mensagens ja carregadas, extraidas da timeline atual. */
    protected timelineMensagens(): TicketMessage[] {
        return this.timeline
            .filter(entrada => entrada.kind === 'MENSAGEM')
            .map(entrada => entrada.mensagem);
    }

    // ─────────────── timeline ───────────────

    eventoIcone(entrada: TimelineEntry): string {
        return eventIcon(entrada.evento?.tipo);
    }

    eventoTexto(entrada: TimelineEntry): string {

        const evento = entrada.evento;
        if (evento == null) {
            return '';
        }

        const titulo = this.ls.instant('tkt.evento.' + evento.tipo);

        // De/para so aparece quando os dois lados existem: "de null para X"
        // seria ruido, e evento de abertura nunca tem o "de".
        if (evento.de != null && evento.para != null) {
            return `${titulo}: ${evento.de} → ${evento.para}`;
        }

        if (evento.para != null) {
            return `${titulo}: ${evento.para}`;
        }

        return titulo;
    }

    autorCor(entrada: TimelineEntry): string {
        return authorColor(entrada.mensagem?.autor?.tipo, entrada.mensagem?.interna === true);
    }

    autorNome(entrada: TimelineEntry): string {

        const autor = entrada.mensagem?.autor;
        if (autor == null) {
            return '';
        }

        if (autor.nome != null && autor.nome.trim().length > 0) {
            return autor.nome;
        }

        return this.ls.instant('tkt.autor.' + autor.tipo);
    }

    /** Alinha a mensagem do cliente de um lado e a nossa do outro. */
    mensagemDoCliente(entrada: TimelineEntry): boolean {
        return entrada.mensagem?.autor?.tipo === 'CLIENTE';
    }

    // ─────────────── anexos ───────────────

    anexosDe(entrada: TimelineEntry): TicketAttachment[] {
        return entrada.mensagem?.anexos ?? [];
    }

    /**
     * Anexo vira link so quando ha para onde apontar.
     *
     * O `ref` e um ponteiro para onde o arquivo foi parar, e quem o preenche e
     * o fluxo de entrada do canal. Se ele guardou uma URL, o anexo abre; se
     * guardou um id interno, o nome aparece sem link. Montar um href com um id
     * que nao e endereco daria um link quebrado, que e pior que nenhum.
     */
    linkDoAnexo(anexo: TicketAttachment): string {

        const ref = (anexo?.ref ?? '').trim();

        if (ref.startsWith('http://') || ref.startsWith('https://')) {
            return ref;
        }

        return null;
    }

    tamanhoDoAnexo(anexo: TicketAttachment): string {

        const bytes = anexo?.tamanho;

        if (bytes == null || bytes <= 0) {
            return '';
        }

        if (bytes < 1024) {
            return `${bytes} B`;
        }

        if (bytes < 1024 * 1024) {
            return `${Math.round(bytes / 1024)} KB`;
        }

        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    /** Icone por familia de mime: imagem, audio, video, documento. */
    iconeDoAnexo(anexo: TicketAttachment): string {

        const mime = (anexo?.mime ?? '').toLowerCase();

        if (mime.startsWith('image/')) {
            return 'file-image';
        }

        if (mime.startsWith('audio/')) {
            return 'audio';
        }

        if (mime.startsWith('video/')) {
            return 'video-camera';
        }

        if (mime.indexOf('pdf') >= 0) {
            return 'file-pdf';
        }

        return 'paper-clip';
    }

    // ─────────────── rotulos do chamado ───────────────

    /**
     * Cor do status na lista. Vermelho e reservado para o que exige acao:
     * chamado esperando atendimento e chamado com SLA estourado.
     */
    statusColor(ticket: Ticket): string {

        if (ticket?.sla?.solucaoEstourou === true || ticket?.sla?.respostaEstourou === true) {
            return 'red';
        }

        switch (ticket?.status) {
            case 'AGUARDANDO_ATENDIMENTO': return 'volcano';
            case 'EM_ATENDIMENTO': return 'blue';
            case 'BOT': return 'purple';
            case 'AGUARDANDO_CLIENTE': return 'gold';
            case 'PAUSADO': return 'default';
            case 'RESOLVIDO': return 'green';
            case 'ENCERRADO': return 'default';
            case 'CANCELADO': return 'default';
            default: return 'default';
        }
    }

    /** Rotulo traduzido do status: as chaves seguem tkt.status.<STATUS>. */
    statusLabel(status: TicketStatus): string {
        if (status == null) {
            return '';
        }
        return this.ls.instant('tkt.status.' + status);
    }

    prioridadeLabel(prioridade: string): string {
        if (prioridade == null) {
            return '';
        }
        return this.ls.instant('tkt.prioridade.' + prioridade);
    }

    canalLabel(ticket: Ticket): string {
        const canal = ticket?.origem?.canal;
        if (canal == null) {
            return '';
        }
        return this.ls.instant('tkt.canal.' + canal);
    }
}
